import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  useMediaQuery,
  Skeleton,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Category, Devices, ShoppingBag } from '@mui/icons-material';
import { useGetNavigation } from '../../../hooks/storefront/useGetNavigation';
import SectionTitle from './shared/SectionTitle';

const CategoriesGrid = ({ section, isLoading: sectionLoading }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  // Get main navigation data as a fallback
  const { isLoading: navigationLoading, navigation } = useGetNavigation();

  const isLoading = sectionLoading || (!section && navigationLoading);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine items based on section or navigation
  const items = React.useMemo(() => {
    if (section?.items) {
      return section.items.map(item => ({
        id: item.id,
        name: item.category?.name || item.title || { en: '', ar: '' },
        slug: item.category?.slug || item.link_url?.split('/').pop() || '',
        image_url: item.category?.image?.url || item.image?.url || '',
        icon: item.category?.icon || ''
      }));
    }
    return navigation?.departments || [];
  }, [section, navigation]);

  const columns = section?.config?.columns || 4;
  const title = section?.title || t('home_sections.categories.title');

  const handleCategoryClick = (slug) => {
    navigate(`/search?page=1&category_slugs=${slug}`);
  };

  // Display Skeleton while loading
  if (isLoading) {
    return (
      <Box sx={{ py: 8, }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Skeleton variant="text" width="40%" height={48} sx={{ mx: 'auto', mb: 2 }} />
            <Skeleton variant="text" width="60%" height={24} sx={{ mx: 'auto' }} />
          </Box>
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item}>
                <Card sx={{ height: 300, borderRadius: 2 }}>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton variant="text" height={32} sx={{ mb: 1 }} />
                    <Skeleton variant="text" height={20} sx={{ mb: 2 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  const lang = i18n.language;

  return (
    <Box sx={{ py: 6 }}>
      <Container maxWidth="lg">
        {/* Section Title with Animation */}
        <SectionTitle
          title={title}
          align="center"
        />

        <Grid container spacing={3}>
          {items.slice(0, 8).map((dept) => (
            <Grid item xs={12} sm={6} md={12 / columns} key={dept.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 12px 24px rgba(255,215,0,0.15)',
                  },
                }}
                onClick={() => handleCategoryClick(dept.slug)}
              >
                {/* Hover Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0.05) 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  }}
                />

                {/* Category Image or Icon Fallback */}
                <Box sx={{
                  position: 'relative',
                  pt: '60%',
                  backgroundColor: 'rgba(255,215,0,0.05)',
                  overflow: 'hidden'
                }}>
                  {dept.image_url ? (
                    <CardMedia
                      component="img"
                      image={dept.image_url}
                      alt={dept.name?.[lang] || dept.name?.en}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                      }}
                    />
                  ) : (
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isMobile ? '2rem' : '3rem',
                      color: '#FFD700',
                      background: 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0.05) 100%)',
                    }}>
                      {dept.icon === 'electronics' && <Devices fontSize="inherit" />}
                      {dept.icon === 'fashion' && <ShoppingBag fontSize="inherit" />}
                      {!dept.icon && <Category fontSize="inherit" />}
                    </Box>
                  )}
                </Box>

                <CardContent sx={{ textAlign: 'center', pb: 2, flexGrow: 1, position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      color: theme.palette.text.primary,
                      mb: 1,
                    }}
                  >
                    {dept.name?.[lang] || dept.name?.en}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* View All Categories Button */}
        {!section && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/categories')}
            >
              {t('home_sections.categories.view_all')}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CategoriesGrid;
